import { Component, OnInit, TemplateRef } from '@angular/core';
import { Chart } from 'chart.js';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FeedbackService } from 'src/app/services/feedback.service';
import { MessageServiceService } from 'src/app/services/message-service.service';
import { FeedbackCampaignSubmitRequest, FeedbackQuestionAnswer, UserFeedbackData } from 'src/app/models/feedback.model';
import { ChatMessage } from 'src/app/models/feedback-notification.model';

@Component({
  selector: 'app-feedback',
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss']
})
export class FeedbackComponent implements OnInit {

  private readonly ratingEmojiMap: { [key: number]: string } = {
    1: '😠',
    2: '☹️',
    3: '😐',
    4: '🙂',
    5: '😊'
  };

  fbTypeOptions = [
    { label: 'IP', value: 1 },
    { label: 'OP', value: 2 },
    { label: 'Emergency', value: 3 }
  ];

  typeFilterOptions = [
    { label: 'All', value: -1 },
    { label: 'IP', value: 1 },
    { label: 'OP', value: 2 },
    { label: 'Emergency', value: 3 }
  ];

  resultFilterOptions = [
    { label: 'All', value: '' },
    { label: 'Positive', value: 'POSITIVE' },
    { label: 'Neutral', value: 'NEUTRAL' },
    { label: 'Negative', value: 'NEGATIVE' }
  ];

  selectedTypeFilter = -1;
  selectedResultFilter = '';
  loading = false;
  feedbackList: UserFeedbackData[] = [];

  positiveCount = 0;
  negativeCount = 0;
  neutralCount = 0;
  positivePercent = 0;
  negativePercent = 0;
  neutralPercent = 0;

  chart: Chart;
  modalRef: BsModalRef | null = null;
  selectedQuestionAnswers: FeedbackQuestionAnswer[] = [];
  selectedFeedbackItem: UserFeedbackData | null = null;

  showSendFeedbackModal = false;
  sendFeedbackType: number | null = null;
  sendFeedbackMobileNumbers: string[] = [];
  mobileNumberInput = '';
  isSubmittingFeedbackCampaign = false;

  loadingAgents = false;
  showAgentDropdown = false;
  agentOptions: { label: string; value: number }[] = [];
  selectedAgentId: number | null = null;
  assigningAgent = false;
  pendingAssignmentFeedbackIds = new Set<number>();

  chatActive = false;
  chatSessionId = '';
  chatPhoneNumber = '';
  chatMessages: ChatMessage[] = [];
  chatFeedbackType = 0;
  chatComments = '';
  chatIsPatientSatisfy: boolean | null = null;
  loadingChat = false;
  readonly CHAT_POLL_INTERVAL_MS = 5000;

  constructor(
    private feedbackService: FeedbackService,
    private messageService: MessageServiceService,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.loadFeedbackData();
  }

  onTypeFilterChange(event: { value: number }): void {
    this.selectedTypeFilter = event ? event.value : this.selectedTypeFilter;
    this.loadFeedbackData();
  }

  onResultFilterChange(event: { value: string }): void {
    this.selectedResultFilter = event ? event.value : this.selectedResultFilter;
    this.loadFeedbackData();
  }

  loadFeedbackData(): void {
    this.loading = true;
    this.feedbackList = [];
    this.feedbackService.getUserFeedbackData(this.selectedTypeFilter, this.selectedResultFilter).subscribe(
      (res) => {
        this.loading = false;
        if (res && res.isSuccess && res.response) {
          this.feedbackList = res.response.userFeedbackDataList || [];
          this.computeAnalysis();
          setTimeout(() => this.renderPieChart(), 0);
        } else {
          this.messageService.showErrorMessage(res && res.message ? res.message : 'No Data Found');
        }
      },
      () => {
        this.loading = false;
        this.messageService.showErrorMessage('Unable to retrieve feedback data from server');
      }
    );
  }

  computeAnalysis(): void {
    this.positiveCount = 0;
    this.negativeCount = 0;
    this.neutralCount = 0;

    this.feedbackList.forEach((item) => {
      const result = (item.result || '').toLowerCase();
      if (result === 'positive') {
        this.positiveCount++;
      } else if (result === 'negative') {
        this.negativeCount++;
      } else {
        this.neutralCount++;
      }
    });

    const total = this.feedbackList.length;
    this.positivePercent = total ? Math.round((this.positiveCount / total) * 100) : 0;
    this.negativePercent = total ? Math.round((this.negativeCount / total) * 100) : 0;
    this.neutralPercent = total ? Math.round((this.neutralCount / total) * 100) : 0;
  }

  renderPieChart(): void {
    const ctx = document.getElementById('feedbackPieChart') as HTMLCanvasElement;
    if (!ctx) {
      return;
    }
    if (this.chart) {
      this.chart.destroy();
    }
    this.chart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['Positive', 'Negative', 'Neutral'],
        datasets: [
          {
            label: 'Feedback Distribution',
            data: [this.positiveCount, this.negativeCount, this.neutralCount],
            backgroundColor: [
              'rgba(40, 167, 69, 0.7)',
              'rgba(220, 53, 69, 0.7)',
              'rgba(54, 162, 235, 0.7)'
            ],
            borderColor: [
              'rgba(40, 167, 69, 1)',
              'rgba(220, 53, 69, 1)',
              'rgba(54, 162, 235, 1)'
            ],
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top'
          }
        }
      }
    });
  }

  fbTypeLabel(fbType: number): string {
    const option = this.fbTypeOptions.find((o) => o.value === fbType);
    return option ? option.label : '-';
  }

  isNegativeOrNeutral(item: UserFeedbackData): boolean {
    const result = (item.result || '').toLowerCase();
    return result === 'negative' || result === 'neutral';
  }

  isNegative(item: UserFeedbackData): boolean {
    return (item.result || '').toLowerCase() === 'negative';
  }

  isAssignmentPending(item: UserFeedbackData): boolean {
    return !item.isAgentAccept && this.pendingAssignmentFeedbackIds.has(item.feedbackId);
  }

  openView(item: UserFeedbackData, template: TemplateRef<any>): void {
    this.selectedFeedbackItem = item;
    this.selectedQuestionAnswers = [];
    this.showAgentDropdown = false;
    this.agentOptions = [];
    this.selectedAgentId = null;
    try {
      const parsed = JSON.parse(item.feedbackResponseJson || '{}');
      this.selectedQuestionAnswers = Object.keys(parsed).map((key) => ({
        question: key,
        answer: parsed[key]
      }));
    } catch (e) {
      this.selectedQuestionAnswers = [];
    }
    this.modalRef = this.modalService.show(template, { class: 'fb-answers-modal' });

    if (item.isAgentAccept) {
      this.loadAgentChat(item);
    }
  }

  private loadAgentChat(item: UserFeedbackData): void {
    this.loadingChat = true;
    this.feedbackService.getFeedbackChatHistory({
      Session_Id: item.sessionId,
      MobileNumber: item.mobileNumber
    }).subscribe(
      (res) => {
        this.loadingChat = false;
        this.chatSessionId = item.sessionId;
        this.chatPhoneNumber = item.mobileNumber;
        this.chatMessages = (res && res.messages) ? res.messages : [];
        this.chatFeedbackType = item.fbType;
        this.chatActive = true;

        this.feedbackService.displayAgentReview({ feedbackId: item.feedbackId }).subscribe(
          (reviewRes) => {
            this.chatComments = (reviewRes && reviewRes.comments) || '';
            this.chatIsPatientSatisfy = reviewRes ? reviewRes.isPatientSatisfy : null;
          },
          () => {
            this.chatComments = '';
            this.chatIsPatientSatisfy = null;
          }
        );
      },
      () => {
        this.loadingChat = false;
        this.messageService.showErrorMessage('Unable to load chat history');
      }
    );
  }

  closeChatPopup(): void {
    this.chatActive = false;
    this.chatSessionId = '';
    this.chatPhoneNumber = '';
    this.chatMessages = [];
    this.chatFeedbackType = 0;
    this.chatComments = '';
    this.chatIsPatientSatisfy = null;
  }

  closeAnswersModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
    this.closeChatPopup();
  }

  openAssignAgent(): void {
    this.loadingAgents = true;
    this.agentOptions = [];
    this.selectedAgentId = null;
    this.feedbackService.getAgentUserNames().subscribe(
      (res) => {
        this.loadingAgents = false;
        if (res && res.isSuccess && res.response) {
          this.agentOptions = (res.response.agentUserNames || []).map((a) => ({
            label: (a.userName || '').split('@')[0],
            value: a.userId
          }));
          this.showAgentDropdown = true;
        } else {
          this.messageService.showErrorMessage(res && res.message ? res.message : 'No agents found');
        }
      },
      () => {
        this.loadingAgents = false;
        this.messageService.showErrorMessage('Unable to retrieve agent list');
      }
    );
  }

  private getLoggedInUserId(): number {
    return +(localStorage.getItem('LOGGED_USER_CRMID') || 0);
  }

  onAgentSelected(event: { value: number }): void {
    const agentId = event ? event.value : this.selectedAgentId;
    if (agentId == null || !this.selectedFeedbackItem) {
      return;
    }
    const feedbackItem = this.selectedFeedbackItem;
    this.selectedAgentId = agentId;
    this.assigningAgent = true;
    this.feedbackService.updateFeedbackAgentAssignment({
      feedbackId: feedbackItem.feedbackId,
      assignedTo: agentId,
      assignedBy: this.getLoggedInUserId()
    }).subscribe(
      () => {
        this.assigningAgent = false;
        this.showAgentDropdown = false;
        this.pendingAssignmentFeedbackIds.add(feedbackItem.feedbackId);
        this.messageService.showSuccess('Agent assigned successfully');
        this.closeAnswersModal();
      },
      () => {
        this.assigningAgent = false;
        this.messageService.showErrorMessage('Unable to assign agent');
      }
    );
  }

  formatFeedbackAnswer(answer: any): string {
    const numericAnswer = Number(answer);
    if (!Number.isNaN(numericAnswer) && this.ratingEmojiMap[numericAnswer]) {
      return this.ratingEmojiMap[numericAnswer];
    }

    return answer == null || answer === '' ? '-' : String(answer);
  }

  openSendFeedbackModal(): void {
    this.sendFeedbackType = null;
    this.sendFeedbackMobileNumbers = [];
    this.mobileNumberInput = '';
    this.showSendFeedbackModal = true;
  }

  closeSendFeedbackModal(): void {
    if (this.isSubmittingFeedbackCampaign) { return; }
    this.showSendFeedbackModal = false;
    this.sendFeedbackType = null;
    this.sendFeedbackMobileNumbers = [];
    this.mobileNumberInput = '';
  }

  isMobileNumberInputValid(): boolean {
    return /^\d+$/.test(this.mobileNumberInput.trim());
  }

  addMobileNumber(): void {
    const number = this.mobileNumberInput.trim();
    if (!this.isMobileNumberInputValid() || this.sendFeedbackMobileNumbers.includes(number)) {
      return;
    }
    this.sendFeedbackMobileNumbers = [...this.sendFeedbackMobileNumbers, number];
    this.mobileNumberInput = '';
  }

  removeMobileNumber(number: string): void {
    this.sendFeedbackMobileNumbers = this.sendFeedbackMobileNumbers.filter((n) => n !== number);
  }

  submitFeedbackCampaign(): void {
    // Include a valid number still sitting in the input that the user typed but didn't explicitly add
    if (this.isMobileNumberInputValid()) {
      this.addMobileNumber();
    }

    if (!this.sendFeedbackType || this.sendFeedbackMobileNumbers.length === 0) {
      this.messageService.showErrorMessage('Please select a type and add at least one mobile number');
      return;
    }

    this.isSubmittingFeedbackCampaign = true;
    const payload: FeedbackCampaignSubmitRequest = {
      feedback_type: this.sendFeedbackType.toString(),
      phone_numbers: this.sendFeedbackMobileNumbers
    };

    this.feedbackService.submitFeedbackCampaign(payload).subscribe(
      () => {
        this.isSubmittingFeedbackCampaign = false;
        this.messageService.showSuccess('Feedback request sent successfully');
        this.showSendFeedbackModal = false;
        this.sendFeedbackType = null;
        this.sendFeedbackMobileNumbers = [];
        this.mobileNumberInput = '';
      },
      () => {
        this.isSubmittingFeedbackCampaign = false;
        this.messageService.showErrorMessage('Unable to send feedback request');
      }
    );
  }
}
